<template>
  <v-navigation-drawer
    :value="value"
    app
    right
    temporary
    hide-overlay
    stateless
    width="320"
    class="user-kpi-panel"
    @input="$emit('input', $event)"
  >
    <div class="d-flex flex-column h-full">
      <div class="user-kpi-panel__header d-flex align-center px-3 py-2">
        <v-btn
          fab
          small
          icon
          :ripple="false"
          @click="$emit('input', false)"
        >
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>

        <div class="ml-2">
          <div class="text-subtitle-1 font-weight-bold">
            {{ userName }}
            <span
              v-if="roleText"
              class="text-caption secondary--text ml-1"
            >{{ roleText }}</span>
          </div>
          <div class="text-caption secondary--text">
            {{ date }}
          </div>
        </div>

        <v-spacer />
      </div>

      <v-divider />

      <div class="user-kpi-panel__body flex-grow-1 pa-4">
        <v-skeleton-loader
          v-if="isLoading"
          type="article, table"
        />

        <template v-else>
          <div class="d-flex mb-6">
            <div class="kpi-summary">
              <div class="kpi-summary__label">
                受領本数 合計
              </div>
              <div class="kpi-summary__value">
                {{ vendingTotal.toLocaleString() }}
              </div>
            </div>
            <div class="kpi-summary">
              <div class="kpi-summary__label">
                売上 合計
              </div>
              <div class="kpi-summary__value">
                ¥{{ salesTotal.toLocaleString() }}
              </div>
            </div>
          </div>

          <div
            v-if="vending.length"
            class="kpi-section"
          >
            <div class="kpi-section__head">
              <span class="kpi-section__swatch kpi-section__swatch--vending" />
              <span class="kpi-section__name">受領本数</span>
              <span class="kpi-section__stat">
                {{ vending.length }}項目 ・ 合計 <strong>{{ vendingTotal.toLocaleString() }}</strong>
              </span>
            </div>
            <table class="kpi-table">
              <thead>
                <tr>
                  <th>評価対象</th>
                  <th class="num">
                    本数
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in vending"
                  :key="`vending-${row.text}`"
                >
                  <td>{{ row.text }}</td>
                  <td class="num">
                    {{ Number(row.value).toLocaleString() }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            v-if="sales.length"
            class="kpi-section"
          >
            <div class="kpi-section__head">
              <span class="kpi-section__swatch kpi-section__swatch--sales" />
              <span class="kpi-section__name">売上</span>
              <span class="kpi-section__stat">
                {{ sales.length }}項目 ・ 合計 <strong>¥{{ salesTotal.toLocaleString() }}</strong>
              </span>
            </div>
            <table class="kpi-table">
              <thead>
                <tr>
                  <th>評価対象</th>
                  <th class="num">
                    金額
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in sales"
                  :key="`sales-${row.text}`"
                >
                  <td>{{ row.text }}</td>
                  <td class="num">
                    ¥{{ Number(row.value).toLocaleString() }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            v-if="!vending.length && !sales.length"
            class="text-center secondary--text py-8"
          >
            このユーザーのKPIデータはありません
          </div>
        </template>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { computed, onMounted, onUnmounted } from '@vue/composition-api'
import { mdiClose } from '@mdi/js'

const ROLE_LABELS = {
  cast: 'キャスト',
  waiter: 'ボーイ',
  alliance: 'アライアンス',
}

export default {
  name: 'UserKpiPanel',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    date: {
      type: String,
      default: '',
    },
    userName: {
      type: String,
      default: '',
    },
    userRole: {
      type: String,
      default: '',
    },
    vending: {
      type: Array,
      default: () => [],
    },
    sales: {
      type: Array,
      default: () => [],
    },
    vendingTotal: {
      type: Number,
      default: 0,
    },
    salesTotal: {
      type: Number,
      default: 0,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const roleText = computed(() => ROLE_LABELS[props.userRole] || props.userRole)

    const handleKeydown = event => {
      if (!props.value) return

      if (event.key === 'Escape') {
        emit('input', false)
      }
    }

    onMounted(() => {
      window.addEventListener('keydown', handleKeydown)
    })

    onUnmounted(() => {
      window.removeEventListener('keydown', handleKeydown)
    })

    return {
      roleText,
      icons: {
        mdiClose,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.user-kpi-panel__body {
  overflow-y: auto;
}

.kpi-summary {
  flex: 1;
  padding-right: 16px;

  &:not(:last-child) {
    border-right: 1px solid rgba(150, 150, 150, 0.2);
    margin-right: 16px;
  }

  &__label {
    font-size: 12px;
    color: var(--v-secondary-base, #8a8f9e);
    margin-bottom: 2px;
  }

  &__value {
    font-size: 22px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
}

.kpi-section {
  margin-bottom: 24px;

  &__head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__swatch {
    width: 6px;
    height: 18px;
    border-radius: 2px;
    flex-shrink: 0;

    &--vending {
      background: #16b6e0;
    }

    &--sales {
      background: #7c5cff;
    }
  }

  &__name {
    font-size: 14px;
    font-weight: 700;
  }

  &__stat {
    margin-left: auto;
    font-size: 12px;
    color: var(--v-secondary-base, #8a8f9e);

    strong {
      font-weight: 700;
    }
  }
}

.kpi-table {
  width: 100%;
  border-collapse: collapse;

  th {
    font-size: 11px;
    font-weight: 500;
    color: var(--v-secondary-base, #8a8f9e);
    text-align: left;
    padding: 6px 8px;
    border-bottom: 1px solid rgba(150, 150, 150, 0.2);
    white-space: nowrap;

    &.num {
      text-align: right;
    }
  }

  td {
    font-size: 13px;
    padding: 10px 8px;
    border-bottom: 1px dashed rgba(150, 150, 150, 0.18);

    &.num {
      text-align: right;
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
    }
  }

  tbody tr:hover {
    background: rgba(150, 150, 150, 0.08);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}
</style>
